import { ButtonType, DialogFooterButtonsWidget, TextLinkKind, TextLinkWidget } from '@tableau/widgets';
import React from 'react';

import { IdentityPoolService } from '~src/authentication/services/IdentityPoolService';
import { Messages } from '~src/codegen/Localize';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { DomainFamily } from '~src/settings/types/IServerSettingsUnauthenticated';
import { AddUserDialogType, AddUsersButton, triggerAddUsersButtonAction } from '~src/userManagement/components/AddUsersButton';

import { AddUsersFromIdentityPoolButtonProps } from './AddUsersFromIdentityPoolButton';
import { IdentityPoolsPicker } from './IdentityPoolsPicker';

export interface AddUsersFromIdentityPoolDialogProps extends AddUsersFromIdentityPoolButtonProps {
  dismiss: () => void;
}

export const DIALOG_TESTID = 'add-users-from-identity-pool-dialog';

enum SelectionMode {
  IdentityPool,
  UserAction
}

export function AddUsersFromIdentityPoolDialog(props: AddUsersFromIdentityPoolDialogProps) {
  const defaultIdentityPool = IdentityPoolService.defaultIdentityPool();

  const [selectionMode, setSelectionMode] = React.useState(SelectionMode.IdentityPool);
  const [selectedIdentityPool, setSelectedIdentityPool] = React.useState(defaultIdentityPool);
  const [selectedAddUsersButtonAction, setSelectedAddUsersButtonAction] = React.useState(null as AddUserDialogType);

  function dialogDescription() {
    if (selectionMode === SelectionMode.IdentityPool) {
      return <div style={{ paddingBottom: CommonStyles.Sizes.LargeSpace }}>
        {Messages.identityPools_AddUsersDialog_IdentityPools_Description()}
        &nbsp;
        <TextLinkWidget
        testId={'identity-pool-learn-more'}
        target={'_blank'}
        href={HelpService.getServerHelpUrlByCategory('idpools-main')}
        kind={TextLinkKind.Standalone}>
          {Messages.learnMore()}
        </TextLinkWidget>
      </div>;
    } else {
      return <div style={{ paddingBottom: CommonStyles.Sizes.LargeSpace }}>
        {Messages.identityPools_AddUsersDialog_AddUsersActions_Description({
        IDENTITY_POOL_NAME: selectedIdentityPool.name})}
      </div>;
    }
  }

  function onActionSelected() {
    props.dismiss(); // Close this dialog!

    const addUsersDialogType: AddUserDialogType = selectedIdentityPool.identityStoreType === 'LOCAL' ?
      AddUserDialogType.AddLocalUser : AddUserDialogType.AddADUser;
    /* Call the action and pass on the selected Identity Pool. For sp1 we have only one possible action depending on store type,
       in the future if we enable more actions, we should pass the selectedAddUsersButtonAction */
    triggerAddUsersButtonAction({ ...props, selectedIdentityPool: selectedIdentityPool }, addUsersDialogType);
  }

  function dialogContent() {
    if (selectionMode === SelectionMode.IdentityPool) {
      // Render the identity pools picker drop-down.
      return <IdentityPoolsPicker identityPools={props.identityPools.concat(defaultIdentityPool)}
        onOptionSelected={setSelectedIdentityPool}
        currentSelection={selectedIdentityPool} />;
    } else {
      // Render the different user action button options but within this dialog.
      return <AddUsersButton
        {...props}
        shouldRenderAsForm={true} // We are rendering within the dialog!
        selectedIdentityPool={selectedIdentityPool}
        identityStoreType={selectedIdentityPool.identityStoreType === 'ACTIVE_DIRECTORY' ? DomainFamily.ActiveDirectory : DomainFamily.Local }
        onOptionSelected={setSelectedAddUsersButtonAction}
        currentSelection={selectedAddUsersButtonAction}
      />;

    }
  }

  function nextWasClicked() {
    if (selectionMode === SelectionMode.IdentityPool) {
      setSelectionMode(SelectionMode.UserAction);
      // For sp1 we have only one action, trigger the action and don't show action selection dialog for now.
      onActionSelected();
    }
  }

  function isNextButtonDisabled(): boolean {
    if (selectionMode === SelectionMode.IdentityPool) {
      return selectedIdentityPool === null;
    } else {
      return selectedAddUsersButtonAction === null;
    }
  }

  function dialogFooter() {
    return <DialogFooterButtonsWidget
      buttons={[
        {
          handleClick: props.dismiss,
          buttonType: ButtonType.Outline,
          children: Messages.cancel(),
          testId: `${DIALOG_TESTID}-cancel`,
        },
        {
          handleClick: nextWasClicked,
          buttonType: ButtonType.Primary,
          children: Messages.identityPools_AddUsersDialog_IdentityPools_OkButton(),
          testId: `${DIALOG_TESTID}-next`,
          disabled: isNextButtonDisabled()
        },
      ]}
      testId={`${DIALOG_TESTID}-footer`}
    />;
  }

  return <>
    {dialogDescription()}

    {dialogContent()}

    <div style={{ paddingTop: CommonStyles.Sizes.LargeSpace }}>
      {dialogFooter()}
    </div>
  </>;
}